(() => {
    /**
     * Tabs controller for the S1 2WD features section.
     * Handles expand/collapse animation, sizing, and right-image sync.
     */
    class S12wdFeaturesTabsSection extends HTMLElement {
        /** Initialize tabs after element is attached. */
        connectedCallback() {
            this.initTabs();
        }

        /**
         * Bootstraps all tab interactions and responsive re-measure logic.
         * Guarded to run once per section instance.
         */
        initTabs() {
            if (this.dataset.tabsInited === 'true') return;
            this.dataset.tabsInited = 'true';

            const items = this.querySelectorAll('.Better-Control-left_li');
            const rightImgs = Array.from(this.querySelectorAll('.Better-Control-right-img'));
            if (!items.length) return;

            const isMobileOrPad = () => window.innerWidth <= 1000;
            // Keep mobile inset aligned with CSS `calc(100% - 26px)` behavior.
            const getMobileInset = () => {
                if (!isMobileOrPad()) return 0;
                if (window.innerWidth < 749) return (26 / 750) * window.innerWidth;
                return 26;
            };

            /** Parse paddings from computed style into numeric values. */
            const getBoxNumbers = (styles) => ({
                paddingTop: parseFloat(styles.paddingTop) || 0,
                paddingBottom: parseFloat(styles.paddingBottom) || 0,
                paddingLeft: parseFloat(styles.paddingLeft) || 0,
                paddingRight: parseFloat(styles.paddingRight) || 0
            });

            /** Resolve item width under max-width rules and container constraints. */
            const getWidths = (it, styles) => {
                const parentWidth = it.parentElement
                    ? it.parentElement.getBoundingClientRect().width
                    : it.getBoundingClientRect().width;
                const leftContainer = this.querySelector('.Better-Control-left');
                const leftContainerWidth = leftContainer
                    ? leftContainer.getBoundingClientRect().width
                    : parentWidth;
                const availableWidth = Math.min(parentWidth, leftContainerWidth);

                const maxWidthRaw = (styles.maxWidth || '').trim();
                let maxWidth = leftContainerWidth;
                if (!maxWidthRaw || maxWidthRaw === 'none') {
                    maxWidth = leftContainerWidth;
                } else if (maxWidthRaw.endsWith('%')) {
                    const pct = parseFloat(maxWidthRaw);
                    maxWidth = Number.isFinite(pct) ? (availableWidth * pct) / 100 : leftContainerWidth;
                } else {
                    const px = parseFloat(maxWidthRaw);
                    maxWidth = Number.isFinite(px) ? px : leftContainerWidth;
                }
                const fixedWidth = Math.max(0, Math.min(availableWidth, maxWidth));
                return { fixedWidth, parentWidth, leftContainerWidth };
            };

            const measureTwoHeight = (two, contentWidth, paddingTop, paddingBottom) => {
                // Use a clone to measure wrapped height under constrained width reliably.
                const cloneTwo = two.cloneNode(true);
                cloneTwo.style.position = 'absolute';
                cloneTwo.style.visibility = 'hidden';
                cloneTwo.style.display = 'block';
                cloneTwo.style.width = `${contentWidth}px`;
                cloneTwo.style.boxSizing = 'content-box';
                two.parentNode.appendChild(cloneTwo);

                cloneTwo.offsetHeight;
                const h = cloneTwo.scrollHeight + paddingTop + paddingBottom;
                cloneTwo.remove();
                return Math.ceil(h);
            };

            /** Measure collapsed state width/height in an always-visible clone. */
            const measureOneSize = (one, paddingTop, paddingBottom, paddingLeft, paddingRight, fixedWidth) => {
                // `.Better-Control-left-one` 在 active 时可能被 display:none（Better-Control-left-one-none）隐藏，
                // 直接读 scrollWidth/scrollHeight 会变 0，导致收起时宽高先跳到 0 再被纠正。
                // 用克隆节点测量，保证任何状态下都能得到正确的“收起目标尺寸”。
                const clone = one.cloneNode(true);
                clone.style.position = 'absolute';
                clone.style.visibility = 'hidden';
                clone.style.display = 'flex';
                clone.style.opacity = '0';
                clone.style.pointerEvents = 'none';
                clone.style.width = 'max-content';
                clone.style.maxWidth = 'none';
                one.parentNode.appendChild(clone);

                clone.offsetHeight;
                const h = Math.ceil(clone.scrollHeight + paddingTop + paddingBottom);
                const wRaw = Math.ceil((clone.scrollWidth || clone.getBoundingClientRect().width) + paddingLeft + paddingRight);
                // 收起态宽度可能来自小数布局（getBoundingClientRect().width），为避免出现 1px 缝隙，
                // 在最终收起宽度上 +1px，并用 ceil 后的 fixedWidth 做上限裁剪。
                const fixedWidthCeil = Math.ceil(fixedWidth);
                const w = Math.min(wRaw + 1, fixedWidthCeil);
                clone.remove();
                return { oneHeight: h, oneWidth: w };
            };

            /** Keep right-side visual in sync with currently active tab. */
            const syncRightImages = (activeIndex) => {
                rightImgs.forEach((img) => {
                    const imgIndex = Number(img.dataset.index);
                    const isActiveImg = imgIndex === activeIndex;

                    if (isActiveImg) {
                        // img.style.display = 'block';
                        requestAnimationFrame(() => {
                            img.classList.add('active');
                        });
                    } else {
                        img.classList.remove('active');
                        setTimeout(() => {
                            if (!img.classList.contains('active')) {
                                // img.style.display = 'none';
                            }
                        }, 300);
                    }
                });
            };

            let liSizes = [];
            let clickLock = false;
            let suppressAutoRecalc = false;
            let ignoreObserverUntil = 0;

            const rebuildSizes = () => {
                liSizes = [];
                items.forEach((item) => {
                    const one = item.querySelector('.Better-Control-left-one');
                    const two = item.querySelector('.Better-Control-left-two');
                    if (!one || !two) return;

                    const itemStyles = window.getComputedStyle(item);
                    const { paddingTop, paddingBottom, paddingLeft, paddingRight } = getBoxNumbers(itemStyles);
                    const { fixedWidth } = getWidths(item, itemStyles);
                    // 移动端 active 时 CSS padding 可能为 0，但视觉上内容需要留出一点内缩（与 CSS: calc(100% - 26px) 对齐）
                    const mobileInset = item.classList.contains('active') ? getMobileInset() : 0;
                    const contentWidth = Math.max(fixedWidth - paddingLeft - paddingRight - mobileInset, 0);

                    const { oneHeight, oneWidth } = measureOneSize(one, paddingTop, paddingBottom, paddingLeft, paddingRight, fixedWidth);

                    liSizes.push({
                        item,
                        one,
                        two,
                        oneHeight,
                        oneWidth,
                        fixedWidth,
                        contentWidth,
                        paddingTop,
                        paddingBottom
                    });
                });
            };

            const applySizes = () => {
                rebuildSizes();
                liSizes.forEach((d) => {
                    const { item, oneHeight, oneWidth } = d;
                    // Keep inactive items compact by default; active item is handled below.
                    if (!item.classList.contains('active')) {
                        item.style.height = `${oneHeight}px`;
                        item.style.width = `${oneWidth}px`;
                    }
                });

                const activeIndex = liSizes.findIndex((d) => d.item.classList.contains('active'));
                if (activeIndex >= 0) {
                    const d = liSizes[activeIndex];
                    const { item, two, fixedWidth, contentWidth, paddingTop, paddingBottom } = d;
                    const twoEl = two;
                    twoEl.style.display = 'block';
                    twoEl.style.width = `${contentWidth}px`;
                    item.style.width = `${fixedWidth}px`;
                    item.style.height = `${measureTwoHeight(twoEl, contentWidth, paddingTop, paddingBottom)}px`;
                    syncRightImages(activeIndex);
                }
            };

            const applySizesNoAnimation = () => {
                // 仅在“交互结束后的校准”时使用：允许我们拿到正确宽高，但不再触发第二次宽高动画。
                const list = Array.from(items);
                const prevTransitions = new Map();
                list.forEach((it) => {
                    prevTransitions.set(it, it.style.transition);
                    it.style.transition = 'none';
                });
                try {
                    applySizes();
                } finally {
                    requestAnimationFrame(() => {
                        list.forEach((it) => {
                            const prev = prevTransitions.get(it);
                            it.style.transition = prev === undefined ? '' : prev;
                        });
                    });
                }
            };

            const withSectionHidden = (fn) => {
                // 在一个同步块里隐藏 section，避免“测量用的临时 class 切换”被用户看到。
                const prevVisibility = this.style.visibility;
                const prevPointerEvents = this.style.pointerEvents;
                this.style.visibility = 'hidden';
                this.style.pointerEvents = 'none';
                try {
                    // 强制一次 reflow，确保隐藏立刻生效
                    // eslint-disable-next-line no-unused-expressions
                    this.offsetHeight;
                    return fn();
                } finally {
                    this.style.visibility = prevVisibility;
                    this.style.pointerEvents = prevPointerEvents;
                }
            };

            const setInactiveDomState = (it) => {
                const one = it.querySelector('.Better-Control-left-one');
                const two = it.querySelector('.Better-Control-left-two');
                it.classList.remove('active');
                one?.classList.remove('Better-Control-left-one-none');
                if (two) {
                    two.style.opacity = 0;
                    two.style.display = 'none';
                    two.style.width = '';
                }
            };

            const setActiveDomState = (it) => {
                const one = it.querySelector('.Better-Control-left-one');
                const two = it.querySelector('.Better-Control-left-two');
                it.classList.add('active');
                one?.classList.add('Better-Control-left-one-none');
                if (two) {
                    two.style.display = 'block';
                    two.style.opacity = 0;
                }
            };

            const snapshotUiState = () => {
                const state = Array.from(items).map((it) => {
                    const one = it.querySelector('.Better-Control-left-one');
                    const two = it.querySelector('.Better-Control-left-two');
                    return {
                        it,
                        active: it.classList.contains('active'),
                        oneHidden: !!one?.classList.contains('Better-Control-left-one-none'),
                        twoDisplay: two?.style?.display || '',
                        twoOpacity: two?.style?.opacity || '',
                        twoWidth: two?.style?.width || '',
                        itWidth: it.style.width || '',
                        itHeight: it.style.height || ''
                    };
                });
                return state;
            };

            const restoreUiState = (snapshot) => {
                snapshot.forEach((s) => {
                    const { it } = s;
                    const one = it.querySelector('.Better-Control-left-one');
                    const two = it.querySelector('.Better-Control-left-two');
                    it.classList.toggle('active', s.active);
                    if (one) one.classList.toggle('Better-Control-left-one-none', s.oneHidden);
                    if (two) {
                        two.style.display = s.twoDisplay;
                        two.style.opacity = s.twoOpacity;
                        two.style.width = s.twoWidth;
                    }
                    it.style.width = s.itWidth;
                    it.style.height = s.itHeight;
                });
                // eslint-disable-next-line no-unused-expressions
                this.offsetHeight;
            };

            const measureTargetsForSwitch = (targetIndex) => {
                // 返回：inactiveSizes(Map) + targetActiveMetrics
                return withSectionHidden(() => {
                    const snap = snapshotUiState();

                    // 1) 先把全部切到“非 active”状态，测量收起目标宽高
                    Array.from(items).forEach((it) => setInactiveDomState(it));
                    // eslint-disable-next-line no-unused-expressions
                    this.offsetHeight;
                    rebuildSizes();
                    const inactiveSizes = new Map();
                    liSizes.forEach((d) => {
                        inactiveSizes.set(d.item, { h: d.oneHeight, w: d.oneWidth });
                    });

                    // 2) 再把目标项切到 active，测量展开目标宽高
                    const targetItem = Array.from(items)[targetIndex];
                    setActiveDomState(targetItem);
                    // eslint-disable-next-line no-unused-expressions
                    this.offsetHeight;

                    const activeStyles = window.getComputedStyle(targetItem);
                    const {
                        paddingTop: activePaddingTop,
                        paddingBottom: activePaddingBottom,
                        paddingLeft: activePaddingLeft,
                        paddingRight: activePaddingRight
                    } = getBoxNumbers(activeStyles);
                    const { fixedWidth: activeFixedWidth } = getWidths(targetItem, activeStyles);
                    const mobileInset = getMobileInset();
                    const activeContentWidth = Math.max(activeFixedWidth - activePaddingLeft - activePaddingRight - mobileInset, 0);
                    const two = targetItem.querySelector('.Better-Control-left-two');
                    let activeHeight = 0;
                    if (two) {
                        activeHeight = measureTwoHeight(two, activeContentWidth, activePaddingTop, activePaddingBottom);
                    }

                    const activeMetrics = {
                        fixedWidth: activeFixedWidth,
                        contentWidth: activeContentWidth,
                        height: activeHeight
                    };

                    // 3) 恢复到测量前的 UI（用户肉眼看不见）
                    restoreUiState(snap);

                    return { inactiveSizes, activeMetrics };
                });
            };

            const debounce = (fn, wait = 80) => {
                let t = null;
                return (...args) => {
                    if (t) clearTimeout(t);
                    t = setTimeout(() => fn(...args), wait);
                };
            };

            const scheduleApply = () => {
                if (suppressAutoRecalc) return;
                requestAnimationFrame(() => applySizes());
            };

            // Initial sizes (run twice: once immediately, once after layout/fonts settle).
            scheduleApply();
            setTimeout(scheduleApply, 120);

            Array.from(items).forEach((item, index) => {
                item.addEventListener('click', () => {
                    if (clickLock) return;
                    clickLock = true;

                    const isActive = item.classList.contains('active');

                    // Ensure we are using fresh metrics for current viewport/layout.
                    rebuildSizes();

                    if (isActive) {
                        // Collapse should be a single, stable transition.
                        // 必须在 inactive CSS 下预测量（尤其是移动端 active 时 padding:0），
                        // 否则收起目标宽高偏小，无法回到原来的收起尺寸/位置。
                        suppressAutoRecalc = true;

                        const { inactiveSizes } = measureTargetsForSwitch(index);

                        requestAnimationFrame(() => {
                            liSizes.forEach((d) => {
                                const { item: it, one: o, two: t, oneHeight: h1, oneWidth: w1 } = d;
                                it.classList.remove('active');
                                t.style.opacity = 0;
                                t.style.width = '';
                                o.classList.remove('Better-Control-left-one-none');
                                t.style.display = 'none';
                                const inactive = inactiveSizes.get(it);
                                it.style.height = `${inactive ? inactive.h : h1}px`;
                                it.style.width = `${inactive ? inactive.w : w1}px`;
                            });
                        });

                        const unlock = () => {
                            suppressAutoRecalc = false;
                            clickLock = false;
                            ignoreObserverUntil = (performance?.now?.() || Date.now()) + 400;
                        };

                        const handler = (e) => {
                            if (e.propertyName === 'height' || e.propertyName === 'width') {
                                item.removeEventListener('transitionend', handler);
                                unlock();
                            }
                        };
                        item.addEventListener('transitionend', handler);

                        // Fallback unlock in case transitionend doesn't fire.
                        setTimeout(() => {
                            if (suppressAutoRecalc) {
                                unlock();
                            }
                        }, 750);

                        return;
                    }

                    // Expand / switch：也需要“只做一次”的稳定顺序，避免旧 active 收起时多次触发重算/抖动。
                    suppressAutoRecalc = true;

                    const unlockAfterExpand = (activeItem, contentEl) => {
                        let contentShown = false;
                        const showContent = () => {
                            if (contentShown) return;
                            contentShown = true;
                            contentEl.style.opacity = 1;
                        };

                        const unlock = () => {
                            suppressAutoRecalc = false;
                            clickLock = false;
                            ignoreObserverUntil = (performance?.now?.() || Date.now()) + 400;
                        };

                        const handler = (e) => {
                            if (e.propertyName === 'width') {
                                showContent();
                            }
                            if (e.propertyName === 'height' || e.propertyName === 'width') {
                                activeItem.removeEventListener('transitionend', handler);
                                unlock();
                            }
                        };
                        activeItem.addEventListener('transitionend', handler);

                        // 兜底：如果宽度 transition 没有触发，延时显示内容，避免一直透明
                        setTimeout(() => {
                            showContent();
                        }, 650);

                        // 兜底：防止没有 transitionend 时卡住
                        setTimeout(() => {
                            if (suppressAutoRecalc) {
                                unlock();
                            }
                        }, 900);
                    };

                    const { inactiveSizes, activeMetrics } = measureTargetsForSwitch(index);

                    requestAnimationFrame(() => {
                        // 1) 先把“其它项”一次性收起到目标宽高（顺序固定：先移除 active，再设宽高，再隐藏 two）
                        liSizes.forEach((d) => {
                            const { item: it, one: o, two: t, oneHeight: h1, oneWidth: w1 } = d;
                            if (it === item) return;
                            it.classList.remove('active');
                            const inactive = inactiveSizes.get(it);
                            const targetH = inactive ? inactive.h : h1;
                            const targetW = inactive ? inactive.w : w1;
                            it.style.height = `${targetH}px`;
                            it.style.width = `${targetW}px`;
                            t.style.opacity = 0;
                            t.style.width = '';
                            o.classList.remove('Better-Control-left-one-none');
                            t.style.display = 'none';
                        });

                        // 2) 再展开当前项
                        const current = liSizes.find((d) => d.item === item);
                        if (!current) {
                            suppressAutoRecalc = false;
                            clickLock = false;
                            scheduleApply();
                            return;
                        }

                        const { item: it, one: o, two: t } = current;
                        it.classList.add('active');
                        o.classList.add('Better-Control-left-one-none');
                        t.style.display = 'block';
                        t.style.opacity = 0;

                        // 使用“预测量”的最终目标值，保证一次动画到位（不依赖第二次重算）。
                        requestAnimationFrame(() => {
                            t.style.width = `${activeMetrics.contentWidth}px`;
                            it.style.width = `${activeMetrics.fixedWidth}px`;
                            it.style.height = `${activeMetrics.height}px`;

                            unlockAfterExpand(it, t);
                        });

                        if (isMobileOrPad()) {
                            const imgs = t.querySelectorAll('img');
                            imgs.forEach((img) => {
                                img.addEventListener('load', () => {
                                    // 图片加载时不要打断动画，结束后由 scheduleApply 统一兜底
                                    if (!suppressAutoRecalc) scheduleApply();
                                });
                            });
                        }

                        syncRightImages(index);
                    });
                });
            });

            // Default activate first item (if none already active).
            if (![...items].some((it) => it.classList.contains('active'))) {
                items[0].classList.add('active');
                items[0].querySelector('.Better-Control-left-one')?.classList.add('Better-Control-left-one-none');
                items[0].querySelector('.Better-Control-left-two')?.style && (items[0].querySelector('.Better-Control-left-two').style.display = 'block');
            }
            scheduleApply();

            // Recalculate on viewport/container changes (mobile rotation, responsive breakpoints).
            const onResize = debounce(() => scheduleApply(), 100);
            window.addEventListener('resize', onResize, { passive: true });

            const leftContainer = this.querySelector('.Better-Control-left');
            if (leftContainer && 'ResizeObserver' in window) {
                const ro = new ResizeObserver(() => {
                    const now = (performance?.now?.() || Date.now());
                    if (now < ignoreObserverUntil) return;
                    if (suppressAutoRecalc || clickLock) return;
                    onResize();
                });
                ro.observe(leftContainer);
            }

            // Fonts can change text metrics on mobile; re-apply once ready.
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready
                    .then(() => {
                        scheduleApply();
                    })
                    .catch(() => {});
            }
        }
    }

    if (!customElements.get('s1-2wd-features-tabs-section')) {
        customElements.define('s1-2wd-features-tabs-section', S12wdFeaturesTabsSection);
    }
})();
